import { decoderJWT } from '~/utils';

import {
  ACCESS_TOKEN_EXPIRATION_NAME,
  ACCESS_TOKEN_NAME,
  GUEST_TOKEN_EXPIRATION_NAME,
  GUEST_TOKEN_NAME,
  REFRESH_COEFFICIENT_SECONDS,
} from '~/const/RequestServices/RequestServicesConst';

export type StoredToken = {
  token: string;
  expiration: number;
};

export type AccessTokenState =
  | { status: 'missing' }
  | {
      status: 'valid' | 'refresh-required' | 'expired';
      token: string;
      expiration: number;
      refreshExpiration: number | null;
      canRefresh: boolean;
    };

export type GuestTokenState =
  | { status: 'missing' }
  | {
      status: 'valid' | 'refresh-required' | 'expired';
      token: string;
      expiration: number;
    };

export class TokenStorage {
  private static isClient(): boolean {
    return import.meta.client;
  }

  private static getItem(name: string): string | null {
    if (!this.isClient()) return null;

    try {
      return localStorage.getItem(name);
    } catch {
      return null;
    }
  }

  private static setItem(name: string, value: string): void {
    if (!this.isClient()) return;

    try {
      localStorage.setItem(name, value);
    } catch {
      // localStorage может быть недоступен, например, в приватном режиме браузера.
    }
  }

  private static removeItem(name: string): void {
    if (!this.isClient()) return;

    try {
      localStorage.removeItem(name);
    } catch {
      // Нечего очищать, если storage недоступен.
    }
  }

  private static normalizeExpiration(value?: string | number | null): number | null {
    const expiration = Number(value);
    return Number.isFinite(expiration) && expiration > 0 ? expiration : null;
  }

  private static getJwtPayload(token: string): Record<string, any> | null {
    try {
      return decoderJWT(token) ?? null;
    } catch {
      return null;
    }
  }

  private static getNow(): number {
    return Math.floor(Date.now() / 1000);
  }

  public static getAccessTokenState(): AccessTokenState {
    const token = this.getItem(ACCESS_TOKEN_NAME);

    if (!token) return { status: 'missing' };

    const payload = this.getJwtPayload(token);
    const expiration =
      this.normalizeExpiration(payload?.exp) ??
      this.normalizeExpiration(this.getItem(ACCESS_TOKEN_EXPIRATION_NAME));
    const refreshExpiration = this.normalizeExpiration(payload?.refresh_expiration);

    if (!expiration) {
      return {
        status: 'expired',
        token,
        expiration: 0,
        refreshExpiration,
        canRefresh: Boolean(refreshExpiration && this.getNow() < refreshExpiration),
      };
    }

    const now = this.getNow();
    const canRefresh = Boolean(refreshExpiration && now < refreshExpiration);

    if (now >= expiration) {
      return { status: 'expired', token, expiration, refreshExpiration, canRefresh };
    }

    if (now >= expiration - REFRESH_COEFFICIENT_SECONDS) {
      return {
        status: 'refresh-required',
        token,
        expiration,
        refreshExpiration,
        canRefresh,
      };
    }

    return { status: 'valid', token, expiration, refreshExpiration, canRefresh };
  }

  /** Возвращает только заведомо действующий access-token. */
  public static getAccessToken(): StoredToken | null {
    const state = this.getAccessTokenState();

    if (state.status !== 'valid') return null;

    return { token: state.token, expiration: state.expiration };
  }

  /** Есть ли сохранённая пользовательская сессия, которую ещё можно продолжить. */
  public static hasRefreshableAccessSession(): boolean {
    const state = this.getAccessTokenState();

    return (
      state.status === 'valid' ||
      ((state.status === 'refresh-required' || state.status === 'expired') && state.canRefresh)
    );
  }

  public static setAccessToken(token: string, expiration?: string | number | null): void {
    if (!this.isClient()) return;

    const payload = this.getJwtPayload(token);
    const normalizedExpiration =
      this.normalizeExpiration(payload?.exp) ?? this.normalizeExpiration(expiration);

    this.setItem(ACCESS_TOKEN_NAME, token);

    if (normalizedExpiration) {
      this.setItem(ACCESS_TOKEN_EXPIRATION_NAME, String(normalizedExpiration));
    } else {
      this.removeItem(ACCESS_TOKEN_EXPIRATION_NAME);
    }
  }

  public static clearAccessToken(): void {
    this.removeItem(ACCESS_TOKEN_NAME);
    this.removeItem(ACCESS_TOKEN_EXPIRATION_NAME);
  }

  public static getGuestTokenState(): GuestTokenState {
    const token = this.getItem(GUEST_TOKEN_NAME);

    if (!token) return { status: 'missing' };

    const payload = this.getJwtPayload(token);
    const expiration =
      this.normalizeExpiration(payload?.exp) ??
      this.normalizeExpiration(this.getItem(GUEST_TOKEN_EXPIRATION_NAME));

    if (!expiration) return { status: 'expired', token, expiration: 0 };

    const now = this.getNow();

    if (now >= expiration) return { status: 'expired', token, expiration };

    if (now >= expiration - REFRESH_COEFFICIENT_SECONDS) {
      return { status: 'refresh-required', token, expiration };
    }

    return { status: 'valid', token, expiration };
  }

  public static getGuestToken(): StoredToken | null {
    const state = this.getGuestTokenState();

    if (state.status !== 'valid') return null;

    return { token: state.token, expiration: state.expiration };
  }

  public static setGuestToken(token: string, expiration?: string | number | null): void {
    const payload = this.getJwtPayload(token);
    const normalizedExpiration =
      this.normalizeExpiration(payload?.exp) ?? this.normalizeExpiration(expiration);

    if (!normalizedExpiration) {
      throw new Error('Guest token expiration is invalid');
    }

    this.setItem(GUEST_TOKEN_NAME, token);
    this.setItem(GUEST_TOKEN_EXPIRATION_NAME, String(normalizedExpiration));
  }

  public static clearGuestToken(): void {
    this.removeItem(GUEST_TOKEN_NAME);
    this.removeItem(GUEST_TOKEN_EXPIRATION_NAME);
  }

  public static clearAll(): void {
    this.clearAccessToken();
    this.clearGuestToken();
  }

  public static isAccessAndRefreshExpired(): boolean {
    const state = this.getAccessTokenState();

    if (state.status === 'missing') return true;
    if (state.status === 'valid') return false;

    return !state.canRefresh;
  }

  /** Удаляет cookie старой реализации после миграции на localStorage. */
  public static clearLegacyTokenCookies(): void {
    if (!this.isClient() || typeof document === 'undefined') return;

    for (const name of [
      ACCESS_TOKEN_NAME,
      ACCESS_TOKEN_EXPIRATION_NAME,
      GUEST_TOKEN_NAME,
      GUEST_TOKEN_EXPIRATION_NAME,
    ]) {
      document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
    }
  }
}
